if (!customElements.get('faq-accordion')) {
  customElements.define(
    'faq-accordion',
    class FaqAccordion extends HTMLElement {
      connectedCallback() {
        // Scoped to this element rather than the document, so two accordions on one page
        // (a section plus the klantenservice list, say) never bind each other's items.
        this.items = Array.from(this.querySelectorAll('.faq-item'));
        if (!this.items.length) return;

        this.iconOpen = this.dataset.iconOpen;
        this.iconClosed = this.dataset.iconClosed;
        this.onResize = this.onResize.bind(this);

        this.items.forEach((item, index) => {
          item.addEventListener('click', () => this.toggle(item));

          const question = item.querySelector('.faq-question');
          // A role="button" div does not synthesise a click from the keyboard the way a
          // real <button> does, so Enter and Space have to be wired up by hand.
          if (question) {
            question.addEventListener('keydown', (event) => {
              if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
              event.preventDefault();
              this.toggle(item);
            });
          }

          if (index === 0 && this.dataset.openFirst !== 'false') {
            this.open(item);
          } else {
            this.reset(item);
          }
        });

        window.addEventListener('resize', this.onResize);
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);
      }

      // Viewport changes re-flow the text, so drop the pixel constraint on anything open.
      onResize() {
        this.querySelectorAll('.faq-answer.open').forEach((answer) => {
          clearTimeout(answer.faqReleaseTimer);
          answer.style.maxHeight = 'none';
        });
      }

      switchIcon(icon, src) {
        if (!icon || !src || icon.getAttribute('src') === src) return;
        icon.classList.add('fading');
        setTimeout(() => {
          icon.src = src;
          icon.classList.remove('fading');
        }, 200);
      }

      isOpen(item) {
        const answer = item.querySelector('.faq-answer');
        return Boolean(answer && answer.classList.contains('open'));
      }

      toggle(item) {
        if (this.isOpen(item)) {
          this.close(item);
          return;
        }
        this.items.forEach((other) => {
          if (other !== item && this.isOpen(other)) this.close(other);
        });
        this.open(item);
      }

      open(item) {
        const answer = item.querySelector('.faq-answer');
        if (!answer) return;

        clearTimeout(answer.faqReleaseTimer);
        answer.classList.add('open', 'mt-6');
        answer.style.maxHeight = `${answer.scrollHeight}px`;
        // The measured pixel value is released once the opening transition has finished.
        // A scrollHeight frozen before web fonts settle would otherwise clip the last
        // lines of the answer, because the container is overflow-hidden.
        answer.faqReleaseTimer = setTimeout(() => {
          if (answer.classList.contains('open')) answer.style.maxHeight = 'none';
        }, 350);

        this.switchIcon(item.querySelector('.faq-toggle-icon'), this.iconOpen);
        const question = item.querySelector('.faq-question');
        if (question) question.setAttribute('aria-expanded', 'true');
      }

      close(item) {
        const answer = item.querySelector('.faq-answer');
        if (!answer) return;

        clearTimeout(answer.faqReleaseTimer);
        // Put a concrete height back for one frame so the collapse animates instead of snapping.
        if (answer.style.maxHeight === 'none' || answer.style.maxHeight === '') {
          answer.style.maxHeight = `${answer.scrollHeight}px`;
          void answer.offsetHeight;
        }
        answer.classList.remove('open', 'mt-6');
        requestAnimationFrame(() => {
          answer.style.maxHeight = '0px';
        });

        this.switchIcon(item.querySelector('.faq-toggle-icon'), this.iconClosed);
        const question = item.querySelector('.faq-question');
        if (question) question.setAttribute('aria-expanded', 'false');
      }

      // Closed state without running close(), which would animate a collapse that never happened.
      reset(item) {
        const answer = item.querySelector('.faq-answer');
        if (!answer) return;
        clearTimeout(answer.faqReleaseTimer);
        answer.classList.remove('open', 'mt-6');
        answer.style.maxHeight = null;
        const question = item.querySelector('.faq-question');
        if (question) question.setAttribute('aria-expanded', 'false');
      }
    }
  );
}
